import React, { useState } from 'react';
import UModal from '../UModal/NewUModal';
import * as Icon from 'ubs-icons';
import { COLOR } from '../common/constant';

const CodeBlock = ({ children, codeText }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (!codeText && typeof children === 'string') {
            codeText = children;
        } else if (!codeText) {
            codeText = React.Children.toArray(children)?.map(child => {
                if (typeof child === 'string') return child;
                if (child.props && child.props.children) return child.props.children;
                return '';
            }).join('\n');
        }

        navigator.clipboard.writeText(codeText || '').then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="u_code-block">
            {children}
            <button className="u_code-block-copy" onClick={handleCopy} title="Copy to clipboard">
                {copied ? <Icon.CheckMark size={16} fill={COLOR?.u_green} /> : <Icon.Copy size={16} fill={COLOR?.u_white} />}
            </button>
        </div>
    );
};

const OptionCard = ({ title, steps, code, extra, label, rawCode }) => (
    <div className="u_option-card">
        <div className="u_flex-align u_mb-12">
            <span className="u_fw-600 u_color-title u_font-14">{label}</span>
            <div className="u_ml-auto u_font-12 u_color-dark-gray">{title}</div>
        </div>

        {steps && (
            <ol className="u_pl-20 u_mb-16 u_color-title u_font-13">
                {steps.map((step, idx) => (
                    <li key={idx} className="u_mb-8">{step}</li>
                ))}
            </ol>
        )}

        {code && <CodeBlock codeText={rawCode}>{code}</CodeBlock>}
        {extra}
    </div>
);

const AccordionItem = ({ title, active, toggle, children }) => {
    return (
        <div className={`u_accordion-item ${active ? 'u_open' : ''}`}>
            <div className="u_accordion-item-header" onClick={toggle}>
                <span>{title}</span>
                <span className={`u_flex-shrink u_ml-12 u_lh-0 ${active ? 'u_down-arrow' : ''}`} style={{ transition: 'transform 0.3s' }}>
                    <Icon.DownArrow size={14} />
                </span>
            </div>
            <div className="u_accordion-item-body">
                <div className="">
                    {children}
                </div>
            </div>
        </div>
    );
};

const LinuxDownloadModal = ({ open, close, downloadLink }) => {
    const [activeTab, setActiveTab] = useState('install'); // install | uninstall | help
    const [openAccordion, setOpenAccordion] = useState(null);

    const toggleAccordion = (index) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    const tabs = [
        { id: 'install', label: 'Install & Update' },
        { id: 'uninstall', label: 'Uninstall' },
        { id: 'help', label: 'Common Issues' },
    ];

    return (
        <UModal
            open={open}
            maxWidth={800}
            onClose={close}
            header={
                <h3 className="u_fw-700 u_color-title">Superworks for Linux</h3>
            }
        >
            <div className="u_pt-20 u_px-20 u_small-font">
                <div className="u_flex u_mb-24 u_bg-white u_radius-8 u_p-4 u_b-light" style={{ width: 'fit-content' }}>
                    {tabs?.map(tab => (
                        <div
                            key={tab.id}
                            className={`u_fancy-tab ${activeTab === tab.id ? 'u_active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>

                <div className="u_animate u_fadeIn">
                    {activeTab === 'install' && (
                        <>
                            <div className="u_flex-align u_mb-20 u_bg-thead u_p-16 u_radius-8 u_b-light">
                                <Icon.Info size={24} className="u_mr-12 u_color-blue" />
                                <div>
                                    <div className="u_fw-600 u_color-title u_font-14 u_mb-4">One Script for Everything</div>
                                    <p className="u_font-12 u_color-font">The same installer script works for both fresh installations and future updates.</p>
                                </div>
                            </div>

                            <OptionCard
                                label="Option 1 (Recommended)"
                                title="Download Script"
                                steps={[
                                    <React.Fragment key="1">Download: <a href={downloadLink} target="_blank" rel="noopener noreferrer" className="u_color-blue u_fw-600">install-superworks.sh</a></React.Fragment>,
                                    "Open Terminal in the download folder",
                                    "Run these commands:"
                                ]}
                                rawCode="chmod +x install-superworks.sh && ./install-superworks.sh"
                                code={
                                    <>
                                        <div>chmod +x install-superworks.sh</div>
                                        <div>./install-superworks.sh</div>
                                    </>
                                }
                                extra={
                                    <p className="u_color-font u_font-13 u_mt-12 u_flex-align">
                                        <span className="u_mr-8" role="img">🚀</span>
                                        <span>After installation, launch by running: <span className="u_bg-thead u_p-4 u_radius-3 u_font-12 u_fw-600">superworks</span></span>
                                    </p>
                                }
                            />

                            <OptionCard
                                label="Option 2"
                                title="One-Command Install"
                                rawCode="curl -fsSL -o install-superworks.sh &quot;https://s3.ap-south-1.amazonaws.com/applications.superworks.com/app/linux/install-superworks.sh&quot; && chmod +x install-superworks.sh && ./install-superworks.sh"
                                code={
                                    <>
                                        curl -fsSL -o install-superworks.sh "https://s3.ap-south-1.amazonaws.com/applications.superworks.com/app/linux/install-superworks.sh" \
                                        <br />&nbsp;&& chmod +x install-superworks.sh \
                                        <br />&nbsp;&& ./install-superworks.sh
                                    </>
                                }
                            />
                        </>
                    )}

                    {activeTab === 'uninstall' && (
                        <>
                            <OptionCard
                                label="Option 1"
                                title="Download Script"
                                steps={[
                                    <React.Fragment key="1">Download: <a href="https://s3.ap-south-1.amazonaws.com/applications.superworks.com/app/linux/remove-superworks.sh" target="_blank" rel="noopener noreferrer" className="u_color-blue u_fw-600">remove-superworks.sh</a></React.Fragment>,
                                    "Run these commands:"
                                ]}
                                rawCode="chmod +x remove-superworks.sh && ./remove-superworks.sh"
                                code={
                                    <>
                                        <div>chmod +x remove-superworks.sh</div>
                                        <div>./remove-superworks.sh</div>
                                    </>
                                }
                            />

                            <OptionCard
                                label="Option 2"
                                title="One-Command Uninstall"
                                rawCode="curl -fsSL -o remove-superworks.sh &quot;https://s3.ap-south-1.amazonaws.com/applications.superworks.com/app/linux/remove-superworks.sh&quot; && chmod +x remove-superworks.sh && ./remove-superworks.sh"
                                code={
                                    <>
                                        curl -fsSL -o remove-superworks.sh "https://s3.ap-south-1.amazonaws.com/applications.superworks.com/app/linux/remove-superworks.sh" \
                                        <br />&nbsp;&& chmod +x remove-superworks.sh \
                                        <br />&nbsp;&& ./remove-superworks.sh
                                    </>
                                }
                            />
                        </>
                    )}

                    {activeTab === 'help' && (
                        <div>
                            <p className="u_mb-16 u_color-title u_font">Click on an issue to see the solution.</p>
                            {[
                                {
                                    title: '1) Permission denied',
                                    content: (
                                        <>
                                            <div className="u_mb-8 u_font-13">Make the script executable before running it.</div>
                                            <div className="u_fw-600 u_mb-4 u_font-12">Run:</div>
                                            <CodeBlock codeText="chmod +x install-superworks.sh && ./install-superworks.sh">
                                                <div>chmod +x install-superworks.sh</div>
                                                <div>./install-superworks.sh</div>
                                            </CodeBlock>
                                        </>
                                    )
                                },
                                {
                                    title: '2) Network / DNS issues',
                                    content: (
                                        <ul className="u_pl-20 u_color-title u_font-13">
                                            <li className="u_mb-4">Check your internet connection.</li>
                                            <li className="u_mb-4">Try switching to a different network (e.g., mobile hotspot) to rule out firewall blocking.</li>
                                            <li>If on a corporate VPN, try disconnecting temporarily.</li>
                                        </ul>
                                    )
                                },
                                {
                                    title: '3) Installer asks for sudo password',
                                    content: (
                                        <div className="u_font-13 u_color-title">
                                            This is completely <span className="u_fw-600">normal</span>. The installer needs administrative privileges to place files in system directories (like `/usr/local/bin` or `/opt`).
                                        </div>
                                    )
                                },
                                {
                                    title: '4) “superworks” command not found after install',
                                    content: (
                                        <>
                                            <div className="u_mb-8 u_font-13">Your terminal alias list might not have refreshed.</div>
                                            <div className="u_fw-600 u_mb-4 u_font-12">Fix: Open a new terminal or run:</div>
                                            <CodeBlock codeText="hash -r && which -a superworks">
                                                <div>hash -r</div>
                                                <div>which -a superworks</div>
                                            </CodeBlock>
                                        </>
                                    )
                                }
                            ]?.map((issue, i) => (
                                <AccordionItem
                                    key={i}
                                    title={issue.title}
                                    active={openAccordion === i}
                                    toggle={() => toggleAccordion(i)}
                                >
                                    {issue.content}
                                </AccordionItem>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </UModal>
    );
};

export default LinuxDownloadModal;
